import { useEffect, useRef, useState } from 'react';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ApiError } from '@/api/api-axios';
import { getMeetingDeviceSettings, updateCameraResolution, updateMeetingDeviceSettings, MeetingDeviceSettings, MeetingPeripheralSettings } from '../api/api-setting-meeting';

const resolutions = ['180p', '240p', '360p', '480p', '720p', '960p', '1080p', '1440p', '2160p'];
export function MeetingDeviceSettingsDialog({ meetingId, imei, name, disabled, onSaved }: {
  meetingId: string; imei: string; name: string; disabled?: boolean; onSaved: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false), [loading, setLoading] = useState(false), [saving, setSaving] = useState(false);
  const [data, setData] = useState<MeetingDeviceSettings | null>(null);
  const [draft, setDraft] = useState<MeetingPeripheralSettings>({});
  const [error, setError] = useState(''), [reload, setReload] = useState(0);
  const busy = useRef(false);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true); setError(''); setData(null);
    getMeetingDeviceSettings(meetingId, imei).then(response => {
      if (active) { setData(response.data); setDraft(response.data.settings); }
    }).catch(e => { if (active) setError((e as ApiError)?.data?.error?.message || 'Không tải được cấu hình thiết bị.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, meetingId, imei, reload]);
  const select = (key: keyof MeetingPeripheralSettings, label: string, options: {id: string; name: string}[]) => {
    const value = draft[key] || '';
    return <label className="grid gap-2 text-sm font-medium" key={key}>{label}
      <select aria-label={label} className="h-10 w-full rounded-md border bg-background px-3" value={value}
        disabled={saving || !options.length} onChange={e => setDraft(d => ({...d, [key]: key === "fpsCam" ? Number(e.target.value) : e.target.value}))}>
        <option value="" disabled>{options.length ? 'Chọn thiết bị' : 'Chưa có danh sách từ thiết bị'}</option>
        {value && !options.some(p => p.id === String(value)) && <option value={value} disabled>{value} (không còn trong danh sách)</option>}
        {options.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </label>;
  };
  const changes = Object.fromEntries(Object.entries(draft).filter(([key, value]) => value && value !== data?.settings[key as keyof MeetingPeripheralSettings])) as MeetingPeripheralSettings;
  const save = async () => {
    if (!data || disabled || busy.current || !Object.keys(changes).length) return;
    busy.current = true; setSaving(true); setError('');
    try {
      const {resolutionCam, fpsCam, ...peripherals} = changes;
      if (Object.keys(peripherals).length) await updateMeetingDeviceSettings(meetingId, imei, peripherals);
      if (resolutionCam || fpsCam) await updateCameraResolution(meetingId, resolutionCam, {deviceImei: imei}, fpsCam);
      toast.success('Đã gửi cấu hình cho thiết bị'); setOpen(false); await onSaved().catch(() => {});
    } catch (e) { setError((e as ApiError)?.data?.error?.message || 'Không gửi được cấu hình. Hãy tải lại danh sách và thử lại.'); }
    finally { busy.current = false; setSaving(false); }
  };
  return <Dialog open={open} onOpenChange={v => { if (!busy.current) setOpen(v); }}>
    <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => setOpen(true)} aria-label={`Cài đặt thiết bị ${name}`}>
      <Settings className="size-4 mr-1"/>Cài đặt
    </Button>
    <DialogContent className="sm:max-w-lg">
      <DialogHeader><DialogTitle>Cài đặt thiết bị · {name}</DialogTitle><DialogDescription>Chọn CAM, MIC, LOA và độ phân giải cho thiết bị trong cuộc họp.</DialogDescription></DialogHeader>
      {loading && <p role="status">Đang tải cấu hình…</p>}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {data && <div className="grid gap-4 sm:grid-cols-2">
        {select('fpsCam', 'FPS khi ở màn hình chính', [5,10,15,20,24,25,30,60].map(n => ({id: String(n), name: `${n} fps`})))}
        {select('camDefault', 'Camera', data.cameraDevices)}
        {select('micDefault', 'Micro', data.microphoneDevices)}
        {select('speakerDefault', 'Loa', data.speakerDevices)}
        {select('resolutionCam', 'Độ phân giải khi ở màn hình chính', resolutions.map(id => ({id, name: id === '2160p' ? '4K (2160p)' : id})))}
      </div>}
      <p className="text-xs text-muted-foreground">Ô nhỏ tối đa 180p/15fps; khi lên màn hình chính dùng độ phân giải và FPS đã chọn. Danh sách do thiết bị báo lên định kỳ. Đổi nguồn hoặc độ phân giải giữ nguyên trạng thái bật/tắt CAM và MIC.</p>
      <div className="flex justify-between gap-2"><Button type="button" variant="outline" disabled={loading || saving} onClick={() => setReload(v => v + 1)}>Tải lại danh sách</Button>
        <Button type="button" disabled={disabled || loading || saving || !data || !Object.keys(changes).length} onClick={save}>{saving ? 'Đang gửi…' : 'Lưu cài đặt'}</Button></div>
    </DialogContent>
  </Dialog>;
}
